import { collection, getDocs, query, where } from 'firebase/firestore';
import type { QueryDocumentSnapshot } from 'firebase/firestore';

import { getFirebaseFirestore } from '@/lib/firebase/firestore';
import type { TopicDoc } from '@/types/backend-contracts';
import { buildTopicsQueryKey } from '@/lib/query-cache/query-keys';
import { resolveBrowserQueryCache, type BrowserQueryCache } from '@/lib/query-cache/query-cache';

async function getTopicDocs(cache: BrowserQueryCache = resolveBrowserQueryCache()): Promise<Array<TopicDoc & { id: string }>> {
  const result = await cache.read(
    buildTopicsQueryKey(),
    async () => {
      const snapshot = await getDocs(query(collection(getFirebaseFirestore(), 'topics'), where('status', '==', 'active')));

      return snapshot.docs.map((document: QueryDocumentSnapshot) => ({
        id: document.id,
        ...(document.data() as TopicDoc),
      }));
    },
    {
      staleTimeMs: 5 * 60 * 1000,
      cacheTimeMs: 30 * 60 * 1000,
      tags: ['catalog:topics'],
      invalidateTagsOnFreshFetch: ['question-answer'],
    },
  );

  return result.value;
}

export async function getActiveTopics(options?: { cache?: BrowserQueryCache }): Promise<Array<TopicDoc & { id: string }>> {
  return getTopicDocs(options?.cache);
}

export async function getTopicBySlug(
  slug: string,
  options?: { cache?: BrowserQueryCache },
): Promise<(TopicDoc & { id: string }) | null> {
  const topics = await getTopicDocs(options?.cache);
  return topics.find((topic) => topic.slug === slug || topic.id === slug) ?? null;
}
